import type { Href } from "expo-router";
import { COLORS } from "../../lib/brand";
import { Link, useLocalSearchParams, useRouter } from "expo-router";
import { useEffect, useState } from "react";
import { Platform, Pressable, Text, View } from "react-native";

import { AuthCard } from "./AuthCard";
import { Button, Input, PasswordStrengthMeter, passwordMeetsMinimumStrength } from "../ui";
import {
  acceptInvite,
  clearPendingInviteToken,
  getPendingInviteToken,
  portalHrefAfterAccept,
  setPendingInviteToken,
} from "../../lib/invites";
import { supabase } from "../../lib/supabase";

function firstParam(v: string | string[] | undefined): string | undefined {
  if (v == null) return undefined;
  return Array.isArray(v) ? v[0] : v;
}

const COMPANY_SIZES = ["1-10", "11-50", "51-200", "201-500", "501-1000", "1000+"] as const;
const AMBER = COLORS.AMBER.hex;
const SUBTITLE = "rgba(28,43,26,0.68)";
const PASSWORD_PLACEHOLDER = "8+ characters, including uppercase, digit, and symbol";

export function ClientSignupForm() {
  const router = useRouter();
  const params = useLocalSearchParams<{ email?: string | string[]; token?: string | string[] }>();
  const paramEmail = firstParam(params.email);
  const paramToken = firstParam(params.token);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [confirmBlurred, setConfirmBlurred] = useState(false);
  const [companyName, setCompanyName] = useState("");
  const [companySize, setCompanySize] = useState<(typeof COMPANY_SIZES)[number]>("1-10");
  const [industry, setIndustry] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmMsg, setConfirmMsg] = useState<string | null>(null);
  const [inviteToken, setInviteToken] = useState<string | null>(null);
  const [hoverSize, setHoverSize] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    void (async () => {
      const stored = await getPendingInviteToken();
      if (!mounted) return;
      const t = paramToken ?? stored ?? null;
      setInviteToken(t);
      if (paramEmail) setEmail(paramEmail.trim().toLowerCase());
    })();
  }, [paramEmail, paramToken]);

  const emailLocked = !!paramEmail;
  const inviteFlow = !!inviteToken;

  async function onSubmit() {
    setError(null);
    setConfirmMsg(null);
    const trimmedFirst = firstName.trim();
    const trimmedLast = lastName.trim();
    if (!trimmedFirst || !trimmedLast || !email.trim() || !password) {
      setError("Please complete all required fields.");
      return;
    }
    if (!passwordMeetsMinimumStrength(password)) {
      setError("Password must be at least 8 characters and meet minimum strength.");
      return;
    }
    if (!confirmPassword || confirmPassword !== password) {
      setConfirmBlurred(true);
      setError("Passwords don't match.");
      return;
    }
    if (!inviteFlow && !companyName.trim()) {
      setError("Please complete all required fields.");
      return;
    }

    // user_profiles currently has only full_name (no first_name / last_name
    // columns) — we collect the parts separately for CRM personalization but
    // concatenate for storage. TODO: split full_name in the DB when an admin
    // surface starts needing the separated form.
    const composedFullName = `${trimmedFirst} ${trimmedLast}`;

    setBusy(true);
    try {
      const { data, error: signErr } = await supabase.auth.signUp({
        email: email.trim(),
        password,
        options: {
          data: {
            coravu_role: "client",
            full_name: composedFullName,
            first_name: trimmedFirst,
            last_name: trimmedLast,
            company_name: companyName.trim(),
            company_size: companySize,
            industry: industry.trim(),
          },
        },
      });
      if (signErr) {
        setError(signErr.message);
        return;
      }
      if (!data.session) {
        if (inviteToken) await setPendingInviteToken(inviteToken);
        setConfirmMsg("Check your email to confirm your account, then sign in.");
        return;
      }

      if (inviteToken) {
        try {
          const result = await acceptInvite(inviteToken);
          await clearPendingInviteToken();
          router.replace(portalHrefAfterAccept(result));
          return;
        } catch (e) {
          setError(e instanceof Error ? e.message : "Invite could not be applied.");
          return;
        }
      }

      const { error: completeErr } = await supabase.rpc("complete_client_signup", {
        p_company_name: companyName.trim(),
        p_company_size: companySize,
        p_industry: industry.trim() || null,
      });
      if (completeErr) {
        setError(completeErr.message);
        return;
      }
      router.replace("/client" as Href);
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthCard>
      <Text
        className="mb-2 text-[10px] font-bold uppercase tracking-widest-3"
        style={{ fontFamily: "DMSans_700Bold", color: AMBER, letterSpacing: 2 }}
      >
        CREATE ACCOUNT
      </Text>
      <Text className="text-[28px] leading-tight text-text" style={{ fontFamily: "CormorantGaramond_400Regular" }}>
        Company sign up
      </Text>
      <Text className="mt-2 text-[14px] leading-6" style={{ fontFamily: "DMSans_300Light", color: SUBTITLE }}>
        Run recruiting programs and discover talent through live case work.
      </Text>

      <View className="mt-6 flex-row gap-3">
        <View className="flex-1">
          <Input label="First name" value={firstName} onChangeText={setFirstName} required />
        </View>
        <View className="flex-1">
          <Input label="Last name" value={lastName} onChangeText={setLastName} required />
        </View>
      </View>

      <View className="mt-3">
        <Input
          label="Work email"
          keyboardType="email-address"
          value={email}
          onChangeText={setEmail}
          helper={emailLocked ? "Locked to invite email" : undefined}
          editable={!emailLocked}
          required
        />
      </View>

      <View className="mt-4">
        <Input
          label="Password"
          secureTextEntry
          value={password}
          onChangeText={setPassword}
          placeholder={PASSWORD_PLACEHOLDER}
          required
        />
        <PasswordStrengthMeter password={password} />
      </View>

      <View className="mt-3">
        <Input
          label="Confirm password"
          secureTextEntry
          value={confirmPassword}
          onChangeText={setConfirmPassword}
          placeholder="Re-enter your password"
          helper="Re-enter your password to confirm."
          required
          autoComplete="new-password"
          textContentType="newPassword"
          onBlur={() => setConfirmBlurred(true)}
          error={
            confirmBlurred && confirmPassword.length > 0 && confirmPassword !== password
              ? "Passwords don't match"
              : undefined
          }
        />
      </View>

      {inviteFlow ? (
        <Text className="mb-3 mt-3 text-sm text-text/70" style={{ fontFamily: "DMSans_400Regular" }}>
          {"You're joining an existing company from your invite — no new company signup needed."}
        </Text>
      ) : (
        <>
          <View className="mt-4">
            <Input label="Company" value={companyName} onChangeText={setCompanyName} required />
          </View>

          <Text className="mb-1 mt-4 text-[10px] font-bold uppercase tracking-[0.16em] text-text/65">
            Company size<Text style={{ color: "#C0392B" }}> *</Text>
          </Text>
          <View className="mb-1 flex-row flex-wrap gap-2">
            {COMPANY_SIZES.map((size) => {
              const selected = companySize === size;
              const hovered = Platform.OS === "web" && hoverSize === size;
              return (
                <Pressable
                  key={size}
                  onPress={() => setCompanySize(size)}
                  onHoverIn={Platform.OS === "web" ? () => setHoverSize(size) : undefined}
                  onHoverOut={Platform.OS === "web" ? () => setHoverSize(null) : undefined}
                  className="rounded-pill border px-3 py-1.5"
                  style={{
                    borderColor: selected || hovered ? AMBER : "#D9D2C5",
                    backgroundColor: selected ? "rgba(181,135,58,0.1)" : COLORS.WHITE.hex,
                    ...(Platform.OS === "web" && hovered ? { transform: [{ scale: 1.03 }] } : {}),
                  }}
                >
                  <Text
                    className={`text-xs ${selected ? "font-semibold text-forest" : "text-forest/55"}`}
                    style={{ fontFamily: "DMSans_400Regular" }}
                  >
                    {size}
                  </Text>
                </Pressable>
              );
            })}
          </View>

          <View className="mt-4">
            <Input label="Industry (optional)" value={industry} onChangeText={setIndustry} />
          </View>
        </>
      )}

      {error ? (
        <Text className="mb-3 mt-3 text-sm text-red-600" style={{ fontFamily: "DMSans_400Regular" }}>
          {error}
        </Text>
      ) : null}
      {confirmMsg ? (
        <Text className="mb-3 mt-3 text-sm text-emerald-700" style={{ fontFamily: "DMSans_400Regular" }}>
          {confirmMsg}
        </Text>
      ) : null}

      <View className="mt-4">
        <Button variant="primary" loading={busy} onPress={() => void onSubmit()}>
          CREATE ACCOUNT →
        </Button>
      </View>

      <Link href="/login" asChild>
        <Pressable className="mt-4" style={Platform.OS === "web" ? ({ cursor: "pointer" } as const) : {}}>
          <Text className="text-center text-[13px]" style={{ fontFamily: "DMSans_400Regular", color: AMBER }}>
            Already have an account? Sign in
          </Text>
        </Pressable>
      </Link>
    </AuthCard>
  );
}
